import React, { useState, useEffect } from 'react';
import { Contact } from '../types/Contact';
import { User } from '@supabase/supabase-js';
import { FileText, Copy, Check, RefreshCw, ChevronRight, ChevronDown, Clock } from 'lucide-react';
import SquareLoader from './SquareLoader';
import { supabase } from '../lib/supabase';
import { updateContactCore } from '@gv/core';

interface TLDRGeneratorProps {
    contact: Contact;
    user: User;
    personalProfile?: string;
    onNotification: (message: string, type?: 'success' | 'error') => void;
    onSave?: () => void;
    formData?: Contact;
    setFormData?: (data: Contact | ((prev: Contact) => Contact)) => void;
    hasChanges?: boolean;
    setHasChanges?: (value: boolean) => void;
    onGenerationStateChange?: (isGenerating: boolean) => void;
}

export default function TLDRGenerator({
    contact,
    user,
    personalProfile,
    onNotification,
    onSave,
    formData,
    setFormData,
    hasChanges,
    setHasChanges,
    onGenerationStateChange
}: TLDRGeneratorProps) {
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [tldrHistory, setTldrHistory] = useState<string[]>([]);

    // Use formData if available, otherwise use contact
    const currentContact = formData || contact;

    // Track if we've already attempted generation for this contact to prevent loops
    const [generationAttempted, setGenerationAttempted] = useState<string | null>(null);

    // Reset generation tracking when contact changes
    useEffect(() => {
        setGenerationAttempted(null);
    }, [currentContact.id]);

    // Auto-generate TLDR only when contact is loaded and no TLDR exists
    useEffect(() => {
        // Generate if:
        // 1) We have a contact ID
        // 2) No existing TLDR
        // 3) Not currently generating
        // 4) We haven't already tried for this contact
        // 5) There's at least some basic data to summarize (name OR email/phone/company/personal_details)
        if (currentContact.id &&
            !currentContact.tldr_summary &&
            !isGenerating &&
            (
                currentContact.first_name || currentContact.last_name ||
                currentContact.email || currentContact.phone ||
                currentContact.company || currentContact.personal_details
            ) &&
            generationAttempted !== currentContact.id) {

            console.log('Auto-generating TLDR for contact:', currentContact.first_name, currentContact.last_name, {
                contactId: currentContact.id,
                hasTldr: !!currentContact.tldr_summary,
                isGenerating,
                hasBasicData: !!(currentContact.first_name || currentContact.last_name || currentContact.email || currentContact.phone || currentContact.company || currentContact.personal_details),
                generationAttempted,
                currentTldr: currentContact.tldr_summary
            });
            setGenerationAttempted(currentContact.id);

            // Add a small delay to ensure contact data is fully loaded
            const timer = setTimeout(() => {
                console.log('Starting TLDR generation after delay');
                generateTLDR();
            }, 500);

            return () => clearTimeout(timer);
        } else {
            console.log('Skipping TLDR auto-generation:', {
                hasId: !!currentContact.id,
                hasTldr: !!currentContact.tldr_summary,
                isGenerating,
                hasBasicData: !!(currentContact.first_name || currentContact.last_name || currentContact.email || currentContact.phone || currentContact.company || currentContact.personal_details),
                generationAttempted,
                currentContactId: currentContact.id,
                tldrValue: currentContact.tldr_summary
            });
        }
    }, [currentContact.id, currentContact.tldr_summary, generationAttempted, isGenerating, currentContact.first_name, currentContact.last_name, currentContact.email, currentContact.phone, currentContact.company, currentContact.personal_details]);

    const generateTLDRPrompt = (contactData: any) => {
        return `You are an expert at creating concise, valuable summaries of contact information for relationship-building purposes.

Contact Information:
${JSON.stringify(contactData, null, 2)}

Please generate a TLDR (Too Long; Didn't Read) summary for this contact that includes:
1. Key professional information (role, company, industry)
2. Important relationship context and personal details
3. Notable business/investment relevance
4. Anything that would help someone quickly understand who this person is and why they matter

Keep it concise (2-3 sentences maximum) but informative. Focus on the most important details that would be useful for relationship-building and future outreach.

Return only the TLDR summary, no additional formatting or headers.`;
    };

    const generateTLDR = async () => {
        console.log('🔄 Starting TLDR generation process');
        setIsGenerating(true);
        onGenerationStateChange?.(true); // Notify parent about generation start

        try {
            // Save current TLDR to history if it exists
            if (currentContact.tldr_summary && !tldrHistory.includes(currentContact.tldr_summary)) {
                setTldrHistory(prev => [currentContact.tldr_summary!, ...prev]);
            }

            const contactData = {
                name: `${currentContact.first_name} ${currentContact.last_name}`,
                title: currentContact.title,
                company: currentContact.company || currentContact.company_name || currentContact.business_name,
                email: currentContact.email,
                phone: currentContact.phone,
                notes: currentContact.notes,
                personal_details: currentContact.personal_details,
                tier: currentContact.tier,
                industry: currentContact.industry,
                city: currentContact.city,
                linkedin: currentContact.linkedin_profile_url,
                about_section: currentContact.about_section,
                professional_experience: currentContact.professional_experience,
                education_background: currentContact.education_background,
                skills_expertise: currentContact.skills_expertise,
                achievements: currentContact.achievements,
                personal_interests: currentContact.personal_interests,
                company_details: currentContact.company_details,
                business_description: currentContact.business_description,
                estimated_net_worth: currentContact.estimated_net_worth,
                estimated_annual_income: currentContact.estimated_annual_income,
                investment_interests: currentContact.investment_interests,
                accredited_investor_likelihood: currentContact.accredited_investor_likelihood
            };

            console.log('📤 Sending TLDR generation request to AI', contactData);

            const response = await supabase.functions.invoke('gemini-ai', {
                body: {
                    prompt: generateTLDRPrompt(contactData)
                }
            });

            console.log('📥 Received AI response:', response);

            if (response.error) {
                throw new Error(response.error.message || 'Failed to generate TLDR');
            }

            if (response.data && response.data.response) {
                const tldr = response.data.response.trim();
                console.log('TLDR generated successfully:', tldr);

                // Update the contact with new TLDR
                if (setFormData) {
                    console.log('Updating TLDR in form data');
                    setFormData(prev => ({
                        ...prev,
                        tldr_summary: tldr
                    }));
                    if (setHasChanges) {
                        setHasChanges(true);
                    }

                    // Also save to database immediately to ensure persistence
                    console.log('Also saving TLDR to database');
                    try {
                        await updateContactCore(supabase as any, String(currentContact.id), {
                            tldr_summary: tldr,
                            ai_last_updated: new Date().toISOString() as any,
                        } as any);
                        console.log('TLDR saved to database successfully');
                    } catch (updateError) {
                        console.error('Failed to save TLDR to database:', updateError);
                    }
                } else {
                    // Update database directly if no form state management
                    console.log('Updating TLDR in database directly');
                    await updateContactCore(supabase as any, String(currentContact.id), {
                        tldr_summary: tldr,
                        ai_last_updated: new Date().toISOString() as any,
                    } as any);
                }

            } else {
                throw new Error('No TLDR generated');
            }
        } catch (error) {
            console.error('Error generating TLDR:', error);
            onNotification('Failed to generate TLDR. Please try again.', 'error');
        } finally {
            setIsGenerating(false);
            onGenerationStateChange?.(false); // Notify parent about generation end
        }
    }; const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(currentContact.tldr_summary || '');
            setCopied(true);
            onNotification('TLDR copied to clipboard!', 'success');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            onNotification('Failed to copy to clipboard', 'error');
        }
    };

    if (!personalProfile) {
        return (
            <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-700/30 p-4 rounded-xl">
                <p className="text-yellow-300 text-sm">
                    <strong>Setup Required:</strong> Please configure your personal profile in Settings to enable TLDR generation.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6 rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-light text-white flex items-center text-lg tracking-wide">
                    <FileText className="w-5 h-5 mr-3 text-purple-400" />
                    TLDR
                </h3>
                <div className="flex items-center space-x-2">
                    {tldrHistory.length > 0 && (
                        <button
                            onClick={() => setShowHistory(!showHistory)}
                            className="flex items-center px-3 py-1 text-xs bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                        >
                            {showHistory ? <ChevronDown className="w-3 h-3 mr-1" /> : <ChevronRight className="w-3 h-3 mr-1" />}
                            Previous
                        </button>
                    )}
                    <button
                        onClick={generateTLDR}
                        disabled={isGenerating}
                        className="flex items-center px-3 py-1 text-xs bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-700"
                    >
                        {isGenerating ? (
                            <SquareLoader size="xs" className="mr-1" />
                        ) : (
                            <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        {currentContact.tldr_summary ? 'Regenerate' : 'Generate'} TLDR
                    </button>
                </div>
            </div>

            {/* Current TLDR */}
            <div className="bg-gray-900 p-4 rounded border border-gray-700 mb-4">
                {isGenerating ? (
                    <div className="flex items-center text-gray-400 text-sm">
                        <SquareLoader size="xs" className="mr-2" />
                        Generating TLDR summary...
                    </div>
                ) : currentContact.tldr_summary ? (
                    <div className="text-gray-200 text-sm leading-relaxed">
                        {currentContact.tldr_summary}
                    </div>
                ) : (
                    <div className="text-gray-400 text-sm italic">
                        No TLDR summary yet. Click "Generate TLDR" to create one.
                    </div>
                )}
            </div>

            {/* Copy Button */}
            {currentContact.tldr_summary && !isGenerating && (
                <div className="flex justify-end mb-4">
                    <button
                        onClick={copyToClipboard}
                        className="flex items-center px-3 py-1 text-xs bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors border border-gray-700"
                    >
                        {copied ? (
                            <>
                                <Check className="w-3 h-3 mr-1" />
                                Copied
                            </>
                        ) : (
                            <>
                                <Copy className="w-3 h-3 mr-1" />
                                Copy
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* TLDR History */}
            {showHistory && tldrHistory.length > 0 && (
                <div className="space-y-2">
                    <div className="text-gray-400 text-xs font-medium">Previous TLDRs:</div>
                    {tldrHistory.map((item, index) => (
                        <div key={index} className="bg-gray-900 bg-opacity-50 p-3 rounded border border-gray-700">
                            <div className="text-gray-200 text-sm leading-relaxed">
                                {item}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
